<script setup lang="ts">
const globals = useGlobals()
const { hideContextMenu, contextMenuState, contextMenuTargetSelection } = useContextMenu()
const { isPrimaryClick } = useGestures()
const menu = ref<HTMLDivElement>()
const { contextMenuCollectionViewBackgroundTestId, collectionViewFileContextMenuTestId } = useTestId()
const { currentRootFolder } = useNavigation()

function onClickOutside(event: MouseEvent) {
  if (!event.target)
    return
  if (event.target === menu.value || menu.value?.contains(event.target as HTMLDivElement))
    return
  if (!isPrimaryClick(event))
    return
  hideContextMenu()
}

onMounted(() => document.addEventListener("click", onClickOutside))
</script>

<template>
  <div
    :id="globals.contextMenuId"
    ref="menu"
    class="context-menu"
  >
    <transition
      appear
      mode="out-in"
      enter-active-class="enter-active-class"
      enter-from-class="enter-from-class"
      enter-to-class="enter-to-class"
      leave-active-class="leave-active-class"
      leave-from-class="leave-from-class"
      leave-to-class="leave-to-class"
    >
      <!-- We use a 'key' here because we may have two context menus in between transitions -->
      <context-menu-dropdown
        v-if="contextMenuState.visible"
        :key="`${contextMenuState.x}|${contextMenuState.y}`"
        class="context-menu-dropdown"
        :x="contextMenuState.x"
        :y="contextMenuState.y"
      >
        <context-menu-target-files
          v-if="contextMenuState.type === 'collection-file' && contextMenuTargetSelection && len(contextMenuTargetSelection) > 1"
          :files="contextMenuTargetSelection"
        />
        <context-menu-target-file
          v-else-if="contextMenuState.type === 'collection-file' && contextMenuState.target"
          :data-testid="collectionViewFileContextMenuTestId"
          :file="(contextMenuState.target as StaticFile)"
        />
        <context-menu-target-file
          v-else-if="contextMenuState.type === 'sidebar-favorites-file' && contextMenuState.target"
          :file="(contextMenuState.target as StaticFile)"
        />
        <context-menu-target-search
          v-else-if="contextMenuState.type === 'sidebar-favorites-search' && contextMenuState.target"
          :search="(contextMenuState.target as StaticSearch)"
        />
        <context-menu-target-panel-conversation
          v-else-if="contextMenuState.type === 'sidebar-favorites-conversation'"
          :conversation="(contextMenuState.target as StaticConversation)"
        />
        <context-menu-target-sidebar-user-root
          v-else-if="contextMenuState.type === 'sidebar-user-root'"
          :folder="(contextMenuState.target as StaticFile)"
        />
        <context-menu-target-sidebar-shared-folder
          v-else-if="contextMenuState.type === 'sidebar-shared-folder'"
          :folder="(contextMenuState.target as StaticFile)"
        />
        <context-menu-target-panel-conversation
          v-else-if="contextMenuState.type === 'panel-chats-conversation'"
          :conversation="(contextMenuState.target as StaticConversation)"
        />
        <context-menu-target-path-file
          v-else-if="contextMenuState.type === 'path-file'"
          :file="(contextMenuState.target as StaticFile)"
        />
        <context-menu-target-search
          v-else-if="contextMenuState.type === 'path-search' && contextMenuState.target"
          :search="(contextMenuState.target as StaticSearch)"
        />
        <context-menu-target-collection-background
          v-else-if="contextMenuState.type === 'main-background' && contextMenuState.target"
          :data-testid="contextMenuCollectionViewBackgroundTestId"
          :folder="((contextMenuState.target ?? currentRootFolder) as StaticFile)"
        />
      </context-menu-dropdown>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.context-menu
  @apply z-overlay-layer

.context-menu-dropdown
  @apply transition opacity-100 scale-100

.enter-active-class
  @apply ease-out
.enter-from-class
  @apply transform scale-95 opacity-0
.enter-to-class
  @apply transform scale-100 opacity-100
.leave-active-class
  @apply ease-out
.leave-from-class
  @apply transform scale-100 opacity-100
.leave-to-class
  @apply transform scale-95 opacity-0
</style>
